import { useCallback, useEffect, useRef, useState } from "react";

/** How long a successful submit may wait for the execution before the hint shows. */
export const MESSAGE_SUBMIT_WAITING_HINT_DELAY_MS = 3000;

export interface MessageSubmitLock {
    /** Name of the pressed action button while its submit is in flight, else null. */
    pendingAction: string | null;
    /** True from the click until the message is submitted (or the request fails). */
    locked: boolean;
    /** True when the request succeeded but the form is still open after the delay. */
    showWaitingHint: boolean;
    /**
     * Run `submit` for the pressed `action`. Ignored while another submit is
     * in flight. The lock is released if `submit` throws or its promise rejects
     * (the request failed and the saga showed an error), so the user can retry.
     * A `submit` that returns no promise started no request and is released at once.
     */
    runSubmit: (action: string, submit: () => unknown) => void;
}

/**
 * In-flight state of an interactive message form (ui-design-principle-modals-
 * dialogs, in-flight clause). Answering a message takes two steps: the PATCH
 * that stores the response, then the wait until the execution continues and the
 * message shows as submitted. The form stays locked across both steps, and a
 * hint appears when the second step takes longer than
 * {@link MESSAGE_SUBMIT_WAITING_HINT_DELAY_MS}.
 */
export const useMessageSubmitLock = (submitted: boolean): MessageSubmitLock => {
    const [pendingAction, setPendingAction] = useState<string | null>(null);
    const [showWaitingHint, setShowWaitingHint] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mountedRef = useRef(true);

    const clearTimer = () => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearTimer();
        };
    }, []);

    // Once the message shows as submitted, the form renders read-only, so the
    // in-flight state has done its job.
    useEffect(() => {
        if (submitted) {
            clearTimer();
            setPendingAction(null);
            setShowWaitingHint(false);
        }
    }, [submitted]);

    const release = () => {
        clearTimer();
        if (mountedRef.current) {
            setPendingAction(null);
            setShowWaitingHint(false);
        }
    };

    const runSubmit = useCallback(
        (action: string, submit: () => unknown) => {
            if (pendingAction !== null || submitted) {
                return;
            }
            setPendingAction(action);
            setShowWaitingHint(false);
            const onStored = () => {
                clearTimer();
                timerRef.current = setTimeout(() => {
                    timerRef.current = null;
                    if (mountedRef.current) {
                        setShowWaitingHint(true);
                    }
                }, MESSAGE_SUBMIT_WAITING_HINT_DELAY_MS);
            };
            let result: unknown;
            try {
                result = submit();
            } catch (error) {
                release();
                return;
            }
            if (result && typeof (result as Promise<unknown>).then === "function") {
                (result as Promise<unknown>).then(onStored, release);
            } else {
                // No request started (e.g. final-form stopped the submit on a
                // validation error), so there is nothing to wait for.
                release();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pendingAction, submitted],
    );

    return { pendingAction, locked: pendingAction !== null, showWaitingHint, runSubmit };
};
